import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Settings2, Mail, DollarSign, Shield, ArrowLeft, Wrench, Eye, Search, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { EmailTemplateTable } from "@/components/admin/EmailTemplateTable";
import { LogoUpload } from "@/components/admin/LogoUpload";

export default function AdminSettings() {
  const { isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTestRecap, setSendingTestRecap] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "general");

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    siteName: "Inclusia",
    contactEmail: "info@inclusia.pro",
    supportEmail: "support@inclusia.pro",
    maintenanceMode: false,
    registrationEnabled: true,
    logos: {
      logo: "",
      logoDark: "",
      logoResponsive: "",
      logoResponsiveDark: ""
    }
  });

  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({
    stripePublicKey: "",
    minimumPayoutAmount: 50,
    affiliateCommissionPercent: 10,
    creatorRewardBlogAmount: 10,
    creatorRewardSocialAmount: 5,
    currency: "EUR",
  });

  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    smtpHost: "",
    smtpPort: "587",
    smtpUser: "",
    smtpPassword: "",
    fromEmail: "noreply@inclusia.pro",
    fromName: "Inclusia",
    adminRecapEmail: "gabrielelakhal@gmail.com",
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    requireEmailVerification: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    enableTwoFactor: false,
  });

  // Modes Settings
  const [modesSettings, setModesSettings] = useState({
    maintenance: {
      enabled: false,
      title: "",
      content: ""
    },
    comingSoon: {
      enabled: false,
      title: "",
      content: ""
    }
  });

  // SEO Settings
  const [seoSettings, setSeoSettings] = useState({
    google_analytics_id: "",
    facebook_pixel_id: "",
    custom_head_scripts: "",
    default_title: "Inclusia - Professionisti LGBTQIA+ Friendly",
    default_description: "La directory per trovare professionisti inclusivi e LGBTQIA+ friendly in Italia"
  });

  // SEO Stats
  const [seoStats, setSeoStats] = useState({
    profiles: 0,
    companies: 0,
    events: 0
  });

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadSettings();
    loadModesSettings();
    loadSeoSettings();
    loadSeoStats();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Carica creator_rewards da app_settings
      const { data: settingsData, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'creator_rewards')
        .maybeSingle();

      if (settingsData?.value) {
        const rewards = settingsData.value as { blog_article?: number; social_content?: number };
        setPaymentSettings(prev => ({
          ...prev,
          creatorRewardBlogAmount: rewards.blog_article || 10,
          creatorRewardSocialAmount: rewards.social_content || 5
        }));
      }

      // Carica platform_branding da app_settings
      const { data: brandingData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'platform_branding')
        .maybeSingle();

      if (brandingData?.value) {
        const branding = brandingData.value as { 
          logo?: string;
          logoDark?: string;
          logoResponsive?: string;
          logoResponsiveDark?: string;
        };
        setGeneralSettings(prev => ({
          ...prev,
          logos: {
            logo: branding.logo || "",
            logoDark: branding.logoDark || "",
            logoResponsive: branding.logoResponsive || "",
            logoResponsiveDark: branding.logoResponsiveDark || ""
          }
        }));
      }

      // Carica admin_recap_email da app_settings
      const { data: recapEmailData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'admin_recap_email')
        .maybeSingle();

      if (recapEmailData?.value) {
        const recapEmail = recapEmailData.value as { email?: string };
        setEmailSettings(prev => ({
          ...prev,
          adminRecapEmail: recapEmail.email || "gabrielelakhal@gmail.com"
        }));
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.loadError"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadModesSettings = async () => {
    try {
      const { data: maintenanceData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      const { data: comingSoonData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'coming_soon_mode')
        .maybeSingle();

      if (maintenanceData?.value) {
        const maintenance = maintenanceData.value as { enabled?: boolean; title?: string; content?: string };
        setModesSettings(prev => ({
          ...prev,
          maintenance: {
            enabled: maintenance.enabled || false,
            title: maintenance.title || "",
            content: maintenance.content || ""
          }
        }));
      }

      if (comingSoonData?.value) {
        const comingSoon = comingSoonData.value as { enabled?: boolean; title?: string; content?: string };
        setModesSettings(prev => ({
          ...prev,
          comingSoon: {
            enabled: comingSoon.enabled || false,
            title: comingSoon.title || "",
            content: comingSoon.content || ""
          }
        }));
      }
    } catch (error) {
      console.error("Error loading modes settings:", error);
    }
  };

  const loadSeoSettings = async () => {
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'seo_settings')
        .maybeSingle();

      if (data?.value) {
        const seo = data.value as {
          google_analytics_id?: string;
          facebook_pixel_id?: string;
          custom_head_scripts?: string;
          default_title?: string;
          default_description?: string;
        };
        setSeoSettings({
          google_analytics_id: seo.google_analytics_id || "",
          facebook_pixel_id: seo.facebook_pixel_id || "",
          custom_head_scripts: seo.custom_head_scripts || "",
          default_title: seo.default_title || "Inclusia - Professionisti LGBTQIA+ Friendly",
          default_description: seo.default_description || ""
        });
      }
    } catch (error) {
      console.error("Error loading SEO settings:", error);
    }
  };

  const loadSeoStats = async () => {
    try {
      const [profilesRes, companiesRes, eventsRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('is_verified', true),
        supabase.from('companies').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('events').select('id', { count: 'exact', head: true }).eq('status', 'published')
      ]);

      setSeoStats({
        profiles: profilesRes.count || 0,
        companies: companiesRes.count || 0,
        events: eventsRes.count || 0
      });
    } catch (error) {
      console.error("Error loading SEO stats:", error);
    }
  };

  const sendTestRecap = async () => {
    try {
      setSendingTestRecap(true);
      
      const { data, error } = await supabase.functions.invoke(
        'send-daily-purchases-recap',
        {
          body: { testMode: true },
        }
      );
      
      if (error) throw error;
      
      if (data?.success) {
        toast({
          title: "✅ Test inviato!",
          description: `Email di test inviata a ${emailSettings.adminRecapEmail}`,
        });
      } else {
        throw new Error(data?.error || "Errore durante l'invio");
      }
    } catch (error: any) {
      console.error("Test recap error:", error);
      toast({
        title: "❌ Errore",
        description: error.message || "Impossibile inviare il test",
        variant: "error",
      });
    } finally {
      setSendingTestRecap(false);
    }
  };

  const saveGeneralSettings = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Salva platform_branding in app_settings
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: 'platform_branding',
          value: generalSettings.logos,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success"),
        description: t("admin.settings.messages.generalSaved"),
      });
    } catch (error) {
      console.error("Error saving general settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const savePaymentSettings = async () => {
    try {
      setSaving(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Salva creator_rewards in app_settings con upsert
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: 'creator_rewards',
          value: {
            blog_article: paymentSettings.creatorRewardBlogAmount,
            social_content: paymentSettings.creatorRewardSocialAmount
          },
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success"),
        description: t("admin.settings.messages.paymentsSaved"),
      });
    } catch (error) {
      console.error("Error saving payment settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Salva admin_recap_email in app_settings
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: 'admin_recap_email',
          value: { email: emailSettings.adminRecapEmail },
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success"),
        description: t("admin.settings.messages.emailSaved"),
      });
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveSecuritySettings = async () => {
    try {
      setSaving(true);
      // Save to database
      toast({
        title: t("admin.settings.toast.success"),
        description: t("admin.settings.messages.securitySaved"),
      });
    } catch (error) {
      console.error("Error saving security settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveModesSettings = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      await supabase.from('app_settings').upsert([
        {
          key: 'maintenance_mode',
          value: modesSettings.maintenance,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        },
        {
          key: 'coming_soon_mode',
          value: modesSettings.comingSoon,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }
      ], { onConflict: 'key' });

      toast({
        title: t("admin.settings.toast.success"),
        description: t("admin.settings.messages.modesSaved"),
      });
    } catch (error) {
      console.error("Error saving modes settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveSeoSettings = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: 'seo_settings',
          value: seoSettings,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success"),
        description: "Impostazioni SEO salvate con successo",
      });
    } catch (error) {
      console.error("Error saving SEO settings:", error);
      toast({
        title: t("admin.settings.toast.error"),
        description: t("admin.settings.messages.saveError"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (mode: 'maintenance' | 'comingSoon') => {
    const url = mode === 'maintenance' ? '/maintenance?preview=true' : '/coming-soon?preview=true';
    window.open(url, '_blank');
  };

  const copySitemapUrl = () => {
    navigator.clipboard.writeText("https://inclusia.pro/sitemap.xml");
    toast({
      title: "URL copiato!",
      description: "URL della sitemap copiato negli appunti",
    });
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">{t("common.dashboard")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.settings.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.settings.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <Settings2 className="w-10 h-10 text-gray-600" />
            {t("admin.settings.title")}
          </h1>
        </div>

        <div className="flex gap-2 mb-6">
          <BrutalButton
            variant={activeTab === "general" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("general")}
          >
            <Settings2 className="w-4 h-4 mr-2" />
            {t("admin.settings.tabs.general")}
          </BrutalButton>
          <BrutalButton
            variant={activeTab === "payments" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("payments")}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            {t("admin.settings.tabs.payments")}
          </BrutalButton>
          <BrutalButton
            variant={activeTab === "email" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("email")}
          >
            <Mail className="w-4 h-4 mr-2" />
            {t("admin.settings.tabs.email")}
          </BrutalButton>
          <BrutalButton
            variant={activeTab === "security" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("security")}
          >
            <Shield className="w-4 h-4 mr-2" />
            {t("admin.settings.tabs.security")}
          </BrutalButton>
          <BrutalButton
            variant={activeTab === "modes" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("modes")}
          >
            <Wrench className="w-4 h-4 mr-2" />
            {t("admin.settings.tabs.modes")}
          </BrutalButton>
          <BrutalButton
            variant={activeTab === "seo" ? "primary" : "outline"}
            size="sm"
            onClick={() => setActiveTab("seo")}
          >
            <Search className="w-4 h-4 mr-2" />
            SEO
          </BrutalButton>
        </div>

        {activeTab === "general" && (
            <BrutalCard>
              <div className="space-y-6">
                {/* Logo Branding Section - Spostata all'inizio */}
                <div>
                  <h3 className="font-heading text-xl font-bold mb-2">{t("admin.brandingSection")}</h3>
                  <p className="text-sm text-muted-foreground mb-6">{t("admin.brandingDescription")}</p>
                  
                  <div className="space-y-6">
                    {/* Desktop Logos */}
                    <div>
                      <h4 className="font-heading text-lg font-bold mb-4 text-foreground">Logo Desktop</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <LogoUpload
                          label="Logo Chiaro"
                          description="Versione per modalità chiara"
                          currentUrl={generalSettings.logos.logo}
                          onUploadComplete={(url) => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logo: url } 
                            })
                          }
                          onRemove={() => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logo: "" } 
                            })
                          }
                          imageWidth={200}
                        />

                        <LogoUpload
                          label="Logo Scuro"
                          description="Versione per modalità scura"
                          currentUrl={generalSettings.logos.logoDark}
                          onUploadComplete={(url) => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoDark: url } 
                            })
                          }
                          onRemove={() => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoDark: "" } 
                            })
                          }
                          imageWidth={200}
                        />
                      </div>
                    </div>

                    {/* Mobile Logos */}
                    <div>
                      <h4 className="font-heading text-lg font-bold mb-4 text-foreground">Logo Mobile</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <LogoUpload
                          label="Logo Responsive Chiaro"
                          description="Versione per modalità chiara"
                          currentUrl={generalSettings.logos.logoResponsive}
                          onUploadComplete={(url) => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoResponsive: url } 
                            })
                          }
                          onRemove={() => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoResponsive: "" } 
                            })
                          }
                          imageWidth={200}
                        />

                        <LogoUpload
                          label="Logo Responsive Scuro"
                          description="Versione per modalità scura"
                          currentUrl={generalSettings.logos.logoResponsiveDark}
                          onUploadComplete={(url) => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoResponsiveDark: url } 
                            })
                          }
                          onRemove={() => 
                            setGeneralSettings({ 
                              ...generalSettings, 
                              logos: { ...generalSettings.logos, logoResponsiveDark: "" } 
                            })
                          }
                          imageWidth={200}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Separatore visivo */}
                <div className="border-t-[3px] border-dark pt-6"></div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.general.siteName")}</label>
                  <BrutalInput
                    value={generalSettings.siteName}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, siteName: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.general.contactEmail")}</label>
                  <BrutalInput
                    type="email"
                    value={generalSettings.contactEmail}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, contactEmail: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.general.supportEmail")}</label>
                  <BrutalInput
                    type="email"
                    value={generalSettings.supportEmail}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, supportEmail: e.target.value })}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="registration"
                    checked={generalSettings.registrationEnabled}
                    onCheckedChange={(checked) =>
                      setGeneralSettings({ ...generalSettings, registrationEnabled: checked })
                    }
                  />
                  <Label htmlFor="registration">{t("admin.settings.general.registrationEnabled")}</Label>
                </div>

                <BrutalButton onClick={saveGeneralSettings} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t("admin.settings.buttons.save")}
                </BrutalButton>
              </div>
            </BrutalCard>
        )}

        {activeTab === "payments" && (
            <BrutalCard>
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.payments.minimumPayout")}</label>
                  <BrutalInput
                    type="number"
                    value={paymentSettings.minimumPayoutAmount}
                    onChange={(e) =>
                      setPaymentSettings({ ...paymentSettings, minimumPayoutAmount: Number(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.payments.affiliateCommission")}</label>
                  <BrutalInput
                    type="number"
                    value={paymentSettings.affiliateCommissionPercent}
                    onChange={(e) =>
                      setPaymentSettings({ ...paymentSettings, affiliateCommissionPercent: Number(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.payments.blogReward")}</label>
                  <BrutalInput
                    type="number"
                    value={paymentSettings.creatorRewardBlogAmount}
                    onChange={(e) =>
                      setPaymentSettings({ ...paymentSettings, creatorRewardBlogAmount: Number(e.target.value) })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("admin.settings.payments.blogRewardNote")}</p>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.payments.socialReward")}</label>
                  <BrutalInput
                    type="number"
                    value={paymentSettings.creatorRewardSocialAmount}
                    onChange={(e) =>
                      setPaymentSettings({ ...paymentSettings, creatorRewardSocialAmount: Number(e.target.value) })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("admin.settings.payments.socialRewardNote")}</p>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.payments.currency")}</label>
                  <BrutalInput
                    value={paymentSettings.currency}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, currency: e.target.value })}
                  />
                </div>

                <BrutalButton onClick={savePaymentSettings} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t("admin.settings.buttons.save")}
                </BrutalButton>
              </div>
            </BrutalCard>
        )}

        {activeTab === "email" && (
          <div className="space-y-6">
            <BrutalCard>
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.email.fromEmail")}</label>
                  <BrutalInput
                    type="email"
                    value={emailSettings.fromEmail}
                    onChange={(e) => setEmailSettings({ ...emailSettings, fromEmail: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.email.fromName")}</label>
                  <BrutalInput
                    value={emailSettings.fromName}
                    onChange={(e) => setEmailSettings({ ...emailSettings, fromName: e.target.value })}
                  />
                </div>

                <div className="pt-6 border-t border-border">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    📊 Recap Acquisti Giornaliero
                  </h4>
                  <div>
                    <label className="text-sm font-bold mb-2 block">
                      Email Destinatario Recap
                    </label>
                    <BrutalInput
                      type="email"
                      value={emailSettings.adminRecapEmail}
                      onChange={(e) => setEmailSettings({ ...emailSettings, adminRecapEmail: e.target.value })}
                      placeholder="gabrielelakhal@gmail.com"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Questo indirizzo riceverà ogni giorno alle 18:00 il recap degli acquisti da fatturare. 
                      L'email arriverà marcata come <strong>URGENTE</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <BrutalButton onClick={saveEmailSettings} disabled={saving}>
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {t("admin.settings.buttons.save")}
                  </BrutalButton>
                  
                  <BrutalButton 
                    onClick={sendTestRecap} 
                    disabled={sendingTestRecap}
                    variant="secondary"
                  >
                    {sendingTestRecap ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    📧 Invia Test Recap
                  </BrutalButton>
                </div>
              </div>
            </BrutalCard>

            <EmailTemplateTable />
          </div>
        )}

        {activeTab === "security" && (
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="emailVerification"
                    checked={securitySettings.requireEmailVerification}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({ ...securitySettings, requireEmailVerification: checked })
                    }
                  />
                  <Label htmlFor="emailVerification">{t("admin.settings.security.emailVerification")}</Label>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.security.sessionTimeout")}</label>
                  <BrutalInput
                    type="number"
                    value={securitySettings.sessionTimeout}
                    onChange={(e) =>
                      setSecuritySettings({ ...securitySettings, sessionTimeout: Number(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.security.maxLoginAttempts")}</label>
                  <BrutalInput
                    type="number"
                    value={securitySettings.maxLoginAttempts}
                    onChange={(e) =>
                      setSecuritySettings({ ...securitySettings, maxLoginAttempts: Number(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.security.passwordMinLength")}</label>
                  <BrutalInput
                    type="number"
                    value={securitySettings.passwordMinLength}
                    onChange={(e) =>
                      setSecuritySettings({ ...securitySettings, passwordMinLength: Number(e.target.value) })
                    }
                  />
                </div>

                <BrutalButton onClick={saveSecuritySettings} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t("admin.settings.buttons.save")}
                </BrutalButton>
              </div>
            </BrutalCard>
        )}

        {activeTab === "modes" && (
          <div className="space-y-6">
            {/* Modalità Manutenzione */}
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    🔧 {t("admin.settings.modes.maintenance.title")}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="maintenanceEnabled"
                      checked={modesSettings.maintenance.enabled}
                      onCheckedChange={(checked) =>
                        setModesSettings({
                          ...modesSettings,
                          maintenance: { ...modesSettings.maintenance, enabled: checked }
                        })
                      }
                    />
                    <Label htmlFor="maintenanceEnabled">{t("admin.settings.modes.maintenance.enable")}</Label>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.modes.maintenance.titleLabel")}</label>
                  <BrutalInput
                    value={modesSettings.maintenance.title}
                    onChange={(e) =>
                      setModesSettings({
                        ...modesSettings,
                        maintenance: { ...modesSettings.maintenance, title: e.target.value }
                      })
                    }
                    placeholder="Es: Sito in Manutenzione"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.modes.maintenance.contentLabel")}</label>
                  <RichTextEditor
                    value={modesSettings.maintenance.content}
                    onChange={(html) =>
                      setModesSettings({
                        ...modesSettings,
                        maintenance: { ...modesSettings.maintenance, content: html }
                      })
                    }
                    placeholder={t("admin.settings.modes.maintenance.contentPlaceholder")}
                  />
                </div>

                <div className="flex gap-2">
                  <BrutalButton onClick={saveModesSettings} disabled={saving}>
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {t("admin.settings.buttons.save")}
                  </BrutalButton>
                  <BrutalButton 
                    variant="outline" 
                    onClick={() => handlePreview('maintenance')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t("admin.settings.modes.buttons.preview")}
                  </BrutalButton>
                </div>
              </div>
            </BrutalCard>

            {/* Modalità Presto Online */}
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    🚀 {t("admin.settings.modes.comingSoon.title")}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="comingSoonEnabled"
                      checked={modesSettings.comingSoon.enabled}
                      onCheckedChange={(checked) =>
                        setModesSettings({
                          ...modesSettings,
                          comingSoon: { ...modesSettings.comingSoon, enabled: checked }
                        })
                      }
                    />
                    <Label htmlFor="comingSoonEnabled">{t("admin.settings.modes.comingSoon.enable")}</Label>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.modes.comingSoon.titleLabel")}</label>
                  <BrutalInput
                    value={modesSettings.comingSoon.title}
                    onChange={(e) =>
                      setModesSettings({
                        ...modesSettings,
                        comingSoon: { ...modesSettings.comingSoon, title: e.target.value }
                      })
                    }
                    placeholder="Es: Presto Online"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">{t("admin.settings.modes.comingSoon.contentLabel")}</label>
                  <RichTextEditor
                    value={modesSettings.comingSoon.content}
                    onChange={(html) =>
                      setModesSettings({
                        ...modesSettings,
                        comingSoon: { ...modesSettings.comingSoon, content: html }
                      })
                    }
                    placeholder={t("admin.settings.modes.comingSoon.contentPlaceholder")}
                  />
                </div>

                <div className="flex gap-2">
                  <BrutalButton onClick={saveModesSettings} disabled={saving}>
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {t("admin.settings.buttons.save")}
                  </BrutalButton>
                  <BrutalButton 
                    variant="outline" 
                    onClick={() => handlePreview('comingSoon')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t("admin.settings.modes.buttons.preview")}
                  </BrutalButton>
                </div>
              </div>
            </BrutalCard>
          </div>
        )}

        {activeTab === "seo" && (
          <div className="space-y-6">
            {/* Sitemap Section - RankMath Style */}
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    🗺️ Sitemap XML (Stile RankMath)
                  </h3>
                </div>

                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 rounded-lg border-2 border-primary/20">
                  <p className="text-sm font-bold mb-1">🗂️ Sitemap Index</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    URL da inserire in Google Search Console. Tutti i contenuti puntano a <strong>inclusia.pro</strong>
                  </p>
                  <div className="flex items-center gap-2 mb-4">
                    <code className="flex-1 bg-background px-3 py-2 rounded border text-xs font-mono break-all">
                      https://inclusia.pro/sitemap.xml
                    </code>
                    <BrutalButton size="sm" variant="outline" onClick={copySitemapUrl}>
                      <Copy className="w-4 h-4" />
                    </BrutalButton>
                    <BrutalButton 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open("https://inclusia.pro/sitemap.xml", "_blank")}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </BrutalButton>
                  </div>

                  <div className="bg-background/80 p-3 rounded-lg mb-4">
                    <p className="text-xs font-medium mb-2 flex items-center gap-1">
                      ✨ <span className="text-primary">Nuovo!</span> Visualizzazione user-friendly con XSL Stylesheet
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Le sitemap ora hanno un'interfaccia grafica stile RankMath con tabelle formattate, conteggio elementi, 
                      colori brand Inclusia e navigazione tra le diverse sezioni.
                    </p>
                  </div>

                  <p className="text-sm font-bold mb-2">📄 Sitemap per Sezione</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    <a href="https://inclusia.pro/page-sitemap.xml" target="_blank" rel="noopener" className="bg-background p-2 rounded border hover:border-primary transition-colors flex items-center gap-2">
                      <span className="text-lg">📃</span>
                      <span className="text-foreground hover:text-primary">Pagine Statiche</span>
                    </a>
                    <a href="https://inclusia.pro/professional-sitemap.xml" target="_blank" rel="noopener" className="bg-background p-2 rounded border hover:border-primary transition-colors flex items-center gap-2">
                      <span className="text-lg">👤</span>
                      <span className="text-foreground hover:text-primary">Professionisti</span>
                    </a>
                    <a href="https://inclusia.pro/company-sitemap.xml" target="_blank" rel="noopener" className="bg-background p-2 rounded border hover:border-primary transition-colors flex items-center gap-2">
                      <span className="text-lg">🏢</span>
                      <span className="text-foreground hover:text-primary">Aziende</span>
                    </a>
                    <a href="https://inclusia.pro/event-sitemap.xml" target="_blank" rel="noopener" className="bg-background p-2 rounded border hover:border-primary transition-colors flex items-center gap-2">
                      <span className="text-lg">📅</span>
                      <span className="text-foreground hover:text-primary">Eventi</span>
                    </a>
                    <a href="https://inclusia.pro/category-sitemap.xml" target="_blank" rel="noopener" className="bg-background p-2 rounded border hover:border-primary transition-colors flex items-center gap-2">
                      <span className="text-lg">🏷️</span>
                      <span className="text-foreground hover:text-primary">Categorie</span>
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-primary/10 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-primary">{seoStats.profiles}</p>
                    <p className="text-sm text-muted-foreground">Professionisti</p>
                  </div>
                  <div className="bg-secondary/10 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-secondary-foreground">{seoStats.companies}</p>
                    <p className="text-sm text-muted-foreground">Aziende</p>
                  </div>
                  <div className="bg-accent/10 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold">{seoStats.events}</p>
                    <p className="text-sm text-muted-foreground">Eventi</p>
                  </div>
                </div>

                <BrutalButton 
                  variant="outline" 
                  onClick={() => {
                    loadSeoStats();
                    toast({ title: "Statistiche aggiornate" });
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Aggiorna Statistiche
                </BrutalButton>
              </div>
            </BrutalCard>

            {/* Tracking Scripts Section */}
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    📊 Tracking Scripts
                  </h3>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">Google Analytics ID</label>
                  <BrutalInput
                    value={seoSettings.google_analytics_id}
                    onChange={(e) => setSeoSettings({ ...seoSettings, google_analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Inserisci l'ID di misurazione Google Analytics 4 (es. G-XXXXXXXXXX)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">Facebook Pixel ID</label>
                  <BrutalInput
                    value={seoSettings.facebook_pixel_id}
                    onChange={(e) => setSeoSettings({ ...seoSettings, facebook_pixel_id: e.target.value })}
                    placeholder="1234567890"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Inserisci l'ID del tuo Facebook Pixel (es. 1234567890)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">Script Personalizzati (Head)</label>
                  <textarea
                    value={seoSettings.custom_head_scripts}
                    onChange={(e) => setSeoSettings({ ...seoSettings, custom_head_scripts: e.target.value })}
                    placeholder="<script>...</script>"
                    className="w-full min-h-[120px] px-4 py-3 border-[3px] border-dark rounded-lg font-mono text-sm bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Script aggiuntivi da iniettare nell'header (es. altri pixel, hotjar, etc.)
                  </p>
                </div>
              </div>
            </BrutalCard>

            {/* Meta Tags Default Section */}
            <BrutalCard>
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    🏷️ Meta Tags Default
                  </h3>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">Titolo Default</label>
                  <BrutalInput
                    value={seoSettings.default_title}
                    onChange={(e) => setSeoSettings({ ...seoSettings, default_title: e.target.value })}
                    placeholder="Inclusia - Professionisti LGBTQIA+ Friendly"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">Descrizione Default</label>
                  <textarea
                    value={seoSettings.default_description}
                    onChange={(e) => setSeoSettings({ ...seoSettings, default_description: e.target.value })}
                    placeholder="La directory per trovare professionisti inclusivi..."
                    className="w-full min-h-[80px] px-4 py-3 border-[3px] border-dark rounded-lg bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Max 160 caratteri per una descrizione SEO ottimale
                  </p>
                </div>

                <BrutalButton onClick={saveSeoSettings} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salva Impostazioni SEO
                </BrutalButton>
              </div>
            </BrutalCard>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
